import {
  BookOpen,
  Plus,
  Settings,
  StickyNote,
  UserPlus,
  Wand2
} from "lucide-react";

import {
  buildNavItems,
  type BuildNavItemsInput
} from "@/components/app-sidebar/navItems";
import { caseWorkspacePath } from "@/features/caseIde/routes";
import {
  shortDate,
  snippetOf
} from "@/features/clientProfile/tabs/notes/notesModel";
import {
  CATEGORY_LABEL,
  categoryOf,
  visasOf
} from "@/features/firmKnowledge/model";
import { automationsPath } from "@/features/automations/routes";
import { firmKnowledgePath } from "@/features/firmKnowledge/routes";
import type {
  FirmLibraryEntry,
  FirmSkill
} from "@/features/firmKnowledge/types";

import type { NoteSearchResult } from "../../api/foreignNationals";
import type { ForeignNational } from "../../api/types/foreignNational";
import type { MatterDTO } from "../../api/types/matter";
import { getPetitionerName } from "../../utils/matterDisplay";
import type { SearchResult } from "./types";

const str = (value: unknown): string =>
  typeof value === "string" ? value.trim() : "";

export const initialsFrom = (name: string): string => {
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0][0] ?? "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] ?? "" : "";
  return `${first}${last}`.toUpperCase();
};

export const clientFullName = (fn: ForeignNational): string =>
  [fn.first_name, fn.middle_name, fn.last_name]
    .map(str)
    .filter(Boolean)
    .join(" ") || "Unknown client";

export function clientToResult(fn: ForeignNational): SearchResult {
  const label = clientFullName(fn);
  return {
    id: fn.uid,
    kind: "client",
    label,
    sublabel: str(fn.email) || undefined,
    to: `/clients/${fn.uid}`,
    avatarProfileId: fn.uid,
    initials: initialsFrom(label)
  };
}

const matterClientName = (m: MatterDTO): string => {
  const ind = (m.individual ?? {}) as Record<string, unknown>;
  const first = str(ind.firstName) || str(ind.first_name);
  const last = str(ind.lastName) || str(ind.last_name);
  const full = `${first} ${last}`.trim();
  return full || str(ind.name) || str(ind.fullName) || "Unknown client";
};

const matterVisa = (m: MatterDTO): string => {
  const fromCase = str(m.case?.visa);
  if (fromCase) return fromCase;
  return str((m.individual as Record<string, unknown> | undefined)?.visa);
};

/** Case home path: the IDE workspace, scoped under the client when linked. */
export const matterPath = (m: MatterDTO): string =>
  caseWorkspacePath({
    individualId: m.individual_id,
    foreignNationalId: m.individual?.foreign_national_id ?? null
  });

export function matterToResult(m: MatterDTO): SearchResult {
  const label = matterClientName(m);
  const petitioner = getPetitionerName(m);
  const sublabel = [matterVisa(m), petitioner !== "-" ? petitioner : ""]
    .filter(Boolean)
    .join(" · ");
  return {
    id: m.case_id || m.individual_id,
    kind: "case",
    label,
    sublabel: sublabel || undefined,
    to: matterPath(m),
    avatarProfileId: m.individual?.foreign_national_id ?? undefined,
    initials: initialsFrom(label),
    status: m.case?.status
  };
}

/** A note hit: the matching passage, then "Arjun Mehta · Daniel Cole · Sep 30, 2026". */
export function noteToResult(
  note: NoteSearchResult,
  query: string
): SearchResult {
  const sublabel = [
    note.client_name,
    note.author_name ?? "",
    shortDate(note.created_at)
  ]
    .filter(Boolean)
    .join(" · ");
  return {
    id: note.id,
    kind: "note",
    label: snippetOf(note.body, query),
    sublabel,
    to: `/clients/${
      note.foreign_national_id
    }?tab=notes&note=${encodeURIComponent(note.id)}`,
    icon: StickyNote
  };
}

/** Static "Jump to" targets: sidebar nav plus common create / settings pages. */
export function buildPageResults(input: BuildNavItemsInput): SearchResult[] {
  const nav = buildNavItems(input);
  const fromNav = [...nav.main, ...nav.internal].map(
    (item): SearchResult => ({
      id: item.id,
      kind: "page",
      label: item.label,
      to: item.url,
      icon: item.icon
    })
  );

  if (input.userType !== "Lawyer") return fromNav;

  const isSuperadmin = input.role === "superadmin";
  const extras: SearchResult[] = [
    {
      id: "new-case",
      kind: "page",
      label: "New Case",
      to: "/cases/new",
      icon: Plus,
      action: true
    },
    {
      id: "new-client",
      kind: "page",
      label: "New Client",
      to: "/clients/new",
      icon: UserPlus,
      action: true
    },
    {
      id: "law-firm-settings",
      kind: "page",
      label: "Law Firm Settings",
      to: "/individual/lawfirm",
      icon: Settings,
      action: true
    }
  ];
  if (isSuperadmin) {
    extras.push(
      {
        id: "admin-users",
        kind: "page",
        label: "Users",
        to: "/admin/users",
        icon: Settings
      },
      {
        id: "admin-customers",
        kind: "page",
        label: "Customers",
        to: "/admin/customers",
        icon: Settings
      },
      {
        id: "admin-visa-settings",
        kind: "page",
        label: "Visa Settings",
        to: "/admin/visa-settings",
        icon: Settings
      },
      {
        id: "admin-default-templates",
        kind: "page",
        label: "Default Templates",
        to: "/admin/default-templates",
        icon: Settings
      },
      {
        id: "admin-exhibit-formats",
        kind: "page",
        label: "Exhibit List Formats",
        to: "/admin/exhibit-list-formats",
        icon: Settings
      }
    );
  }
  return [...fromNav, ...extras];
}

/** Case-insensitive substring match on the label; an empty query keeps everything. */
export function filterPages(
  pages: SearchResult[],
  query: string
): SearchResult[] {
  const q = query.trim().toLowerCase();
  if (!q) return pages;
  return pages.filter((p) => p.label.toLowerCase().includes(q));
}

/** Server search only kicks in from this many characters. */
export const MIN_QUERY_LENGTH = 2;

const words = (query: string) =>
  query.trim().toLowerCase().split(/\s+/).filter(Boolean);

/** Every word of the query appears somewhere in the haystack; the title counts double. */
const knowledgeScore = (title: string, rest: string[], query: string) => {
  const t = title.toLowerCase();
  const hay = `${t}\n${rest.join("\n").toLowerCase()}`;
  const ws = words(query);
  if (!ws.length || !ws.every((w) => hay.includes(w))) return 0;
  return (
    ws.reduce((score, w) => score + (t.includes(w) ? 2 : 1), 0) +
    (t.startsWith(ws[0]) ? 1 : 0)
  );
};

const top = <T>(scored: [T, number][], limit: number): T[] =>
  scored
    .filter(([, score]) => score > 0)
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([item]) => item);

/** Library documents matching the query, best first, as palette rows. */
export function searchLibrary(
  entries: FirmLibraryEntry[],
  query: string,
  limit = 6
): SearchResult[] {
  if (query.trim().length < MIN_QUERY_LENGTH) return [];
  const scored = entries.map((entry): [FirmLibraryEntry, number] => [
    entry,
    knowledgeScore(
      entry.name || entry.original_filename || "",
      [
        entry.description,
        entry.original_filename ?? "",
        CATEGORY_LABEL[categoryOf(entry)],
        ...visasOf(entry),
        ...entry.tags
      ],
      query
    )
  ]);
  return top(scored, limit).map((entry) => ({
    id: entry.id,
    kind: "library",
    label: entry.name || entry.original_filename || "Untitled",
    sublabel:
      [CATEGORY_LABEL[categoryOf(entry)], visasOf(entry).join(", ")]
        .filter(Boolean)
        .join(" · ") || undefined,
    to: firmKnowledgePath({ entryId: entry.id }),
    icon: BookOpen
  }));
}

/** Skills (not archived) matching the query, best first, as palette rows. */
export function searchSkills(
  skills: FirmSkill[],
  query: string,
  limit = 5
): SearchResult[] {
  if (query.trim().length < MIN_QUERY_LENGTH) return [];
  const scored = skills
    .filter((s) => s.status !== "archived")
    .map((skill): [FirmSkill, number] => [
      skill,
      knowledgeScore(skill.title, [skill.description, skill.slug], query)
    ]);
  return top(scored, limit).map((skill) => ({
    id: skill.id,
    kind: "skill",
    label: skill.title,
    sublabel: skill.status === "draft" ? "Draft automation" : undefined,
    to: automationsPath({ skillId: skill.id }),
    icon: Wand2
  }));
}
